import { QueryKeys } from '@/enums';
import { openNotification } from '@/helpers/mantine-notification-helper';
import { INextAuthSessionModel } from '@/interfaces/authentication';
import { IAddToWishlist, IWishlist } from '@/interfaces/wishlist';
import { addToWishlist, deleteFromWishlist, getWishlist } from '@/services/wishlist.service';
import { UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useReactQuerySession } from '@/hooks/reactQuerySession';
const getWishlistConfig: UseQueryOptions<any> = {
  queryKey: [QueryKeys.wishlist],
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  retry: false,
};
export const useWishlist = (session?: INextAuthSessionModel | null) => {
  return useQuery<IWishlist>({
    ...getWishlistConfig,
    queryFn: async () => {
      return await getWishlist(session);
    },
  });
};
export const useAddItemToWishlist = (
  doInvalidate = true,
  seName?: string,
  toggleProductWishlistState?: (IsWishListed: boolean, productId: string) => void,
) => {
  const authObject = useReactQuerySession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (wishListItem: IAddToWishlist) => {
      if (authObject.isAuthenticated) {
        return addToWishlist(wishListItem);
      } else {
        return Promise.reject({ data: { Messages: ['You need to login first!'] } });
      }
    },
    onSuccess: (data, wishListItem) => {
      openNotification('success', 'Wishlist', 'Product added to wishlist successfully.');
      if (doInvalidate) {
        queryClient.invalidateQueries({ queryKey: [QueryKeys.wishlist + seName] });
      } else if (typeof toggleProductWishlistState === 'function') {
        toggleProductWishlistState(true, wishListItem.productId);
      }
    },
    onError: (data: any) => {
      openNotification('danger', 'Wishlist', data.data.Messages[0]);
    },
  });
};

export const useDeleteItemFromWishlist = (
  doInvalidate = true,
  seName?: string,
  toggleProductWishlistState?: (IsWishListed: boolean, productId: string) => void,
  accountInvalidate?: boolean,
) => {
  const authObject = useReactQuerySession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (wishlistItemId: string) => {
      if (authObject.isAuthenticated) {
        return deleteFromWishlist(wishlistItemId);
      } else {
        return Promise.reject({ data: { Messages: ['You need to login first!'] } });
      }
    },
    onSuccess: (data, wishlistItemId) => {
      openNotification('success', 'Wishlist', 'Product removed from wishlist successfully.');
      if (doInvalidate) {
        queryClient.invalidateQueries({ queryKey: [QueryKeys.wishlist + seName] });
      } else if (accountInvalidate) {
        queryClient.invalidateQueries({ queryKey: [QueryKeys.wishlist] });
      } else if (typeof toggleProductWishlistState === 'function') {
        toggleProductWishlistState(false, wishlistItemId);
      }
    },
    onError: (data: any) => {
      openNotification('danger', 'Wishlist', data.data.Messages[0]);
    },
  });
};
